/*
 * @Description: 页面说明 自动左右对齐指令
 * @Version: 版本号
 * @Author: chuzhangyi
 * @Date: 2026-01-06 11:21:30
 * @LastEditors: 最后一次编辑说明
 * @LastEditTime: 2026-01-26 14:01:28
 * @FilePath: \admine:\LBB\wechat_business\src\directives\autoAlign.js
 */

// src/directives/autoAlign.js

// 默认配置常量
const DEFAULT_CONFIG = {
  minWidth: '34%',
  maxWidth: '100%',
  lineHeight: '1.5'
}

/**
 * 从元素获取属性值
 * @param {HTMLElement} el - 元素
 * @param {String} name - 属性名
 * @param {*} defaultValue - 默认值
 * @returns {*}
 */
function getAttr(el, name, defaultValue = '') {
  const value = el.getAttribute(name)
  return value !== null ? value : defaultValue
}

/**
 * 规范化配置对象
 * @param {Object} config - 配置对象
 * @returns {Object} 规范化后的配置
 */
function normalizeConfig(config) {
  const normalized = { ...config }

  // 处理 kebab-case 到 camelCase 的转换
  const mappings = {
    'min-width': 'minWidth',
    'max-width': 'maxWidth',
    'line-height': 'lineHeight'
  }

  Object.keys(mappings).forEach(kebabKey => {
    if (normalized[kebabKey] !== undefined) {
      normalized[mappings[kebabKey]] = normalized[kebabKey]
      delete normalized[kebabKey]
    }
  })

  return normalized
}

/**
 * 获取配置选项
 * @param {HTMLElement} el - 指令绑定的元素
 * @param {*} value - 指令的值
 * @returns {Object} 配置对象
 */
function getAutoAlignOptions(el, value) {
  const options = {
    minWidth: getAttr(el, 'auto-align-min-width', DEFAULT_CONFIG.minWidth),
    maxWidth: getAttr(el, 'auto-align-max-width', DEFAULT_CONFIG.maxWidth),
    lineHeight: getAttr(el, 'auto-align-line-height', DEFAULT_CONFIG.lineHeight)
  }

  // 如果是指令值是对象，则合并配置
  if (typeof value === 'object' && value !== null) {
    const normalizedValue = normalizeConfig(value)
    Object.assign(options, normalizedValue)
  }

  return options
}

/**
 * 保存子元素的原始样式
 * @param {HTMLElement} el - 父元素
 * @param {Array} children - 子元素数组
 */
function saveOriginalStyles(el, children) {
  const originalStyles = []

  children.forEach((child, index) => {
    originalStyles.push({
      minWidth: child.style.minWidth || '',
      maxWidth: child.style.maxWidth || '',
      lineHeight: child.style.lineHeight || '',
      textAlign: child.style.textAlign || ''
    })
  })

  el._autoAlignOriginalStyles = originalStyles
}

/**
 * 恢复子元素的原始样式
 * @param {HTMLElement} el - 父元素
 */
function restoreOriginalStyles(el) {
  if (!el || !el._autoAlignOriginalStyles) return

  const children = Array.from(el.children)
  const originalStyles = el._autoAlignOriginalStyles

  children.forEach((child, index) => {
    if (index < originalStyles.length) {
      const original = originalStyles[index]
      if (original.minWidth) {
        child.style.minWidth = original.minWidth
      } else {
        child.style.minWidth = ''
      }

      if (original.maxWidth) {
        child.style.maxWidth = original.maxWidth
      } else {
        child.style.maxWidth = ''
      }

      if (original.lineHeight) {
        child.style.lineHeight = original.lineHeight
      } else {
        child.style.lineHeight = ''
      }

      if (original.textAlign) {
        child.style.textAlign = original.textAlign
      } else {
        child.style.textAlign = ''
      }
    }
  })

  // 清理保存的数据
  delete el._autoAlignOriginalStyles
}

/**
 * 应用自动对齐样式
 * @param {HTMLElement} el - 指令绑定的元素
 * @param {Object} options - 配置选项
 */
function applyAutoAlign(el, options) {
  if (!el) return

  const parentWidth = el.clientWidth
  const parentRect = el.getBoundingClientRect()
  const parentCenter = parentRect.left + parentWidth / 2
  const children = Array.from(el.children)

  if (children.length === 0) return

  // 保存原始样式
  if (!el._autoAlignOriginalStyles) {
    saveOriginalStyles(el, children)
  }

  // 应用样式到每个子元素
  children.forEach((child) => {
    // 设置宽度和行高
    if (options.minWidth) {
      child.style.minWidth = options.minWidth
    }
    if (options.maxWidth) {
      child.style.maxWidth = options.maxWidth
    }
    if (options.lineHeight) {
      child.style.lineHeight = options.lineHeight
    }

    // 计算子元素中心点
    const childWidth = child.clientWidth
    const childRect = child.getBoundingClientRect()
    const childCenter = childRect.left + childWidth / 2

    // 根据子元素中心点相对于父元素中心点的位置，设置文本对齐
    if (parseInt(childCenter) <= parseInt(parentCenter)) {
      child.style.textAlign = 'left'
    } else {
      child.style.textAlign = 'right'
    }
  })
}

/**
 * 使用 ResizeObserver 监听元素大小变化
 * @param {HTMLElement} el - 要监听的元素
 * @param {Function} callback - 回调函数
 * @returns {ResizeObserver} ResizeObserver 实例
 */
function observeResize(el, callback) {
  if (!window.ResizeObserver) {
    // 如果不支持 ResizeObserver，使用 fallback
    return null
  }

  const observer = new ResizeObserver(() => {
    callback()
  })

  observer.observe(el)
  return observer
}

const autoAlignDirective = {
  inserted(el, binding) {
    const { value } = binding

    // 获取配置
    const options = getAutoAlignOptions(el, value)

    // 存储配置到元素
    el.autoAlignOptions = options

    // 等待 DOM 更新后应用样式
    setTimeout(() => {
      applyAutoAlign(el, options)
    }, 0)

    // 监听窗口大小变化
    const handleResize = () => {
      applyAutoAlign(el, options)
    }

    window.addEventListener('resize', handleResize)
    el._autoAlignResizeHandler = handleResize

    // 使用 ResizeObserver 监听元素大小变化（如果支持）
    const resizeObserver = observeResize(el, () => {
      applyAutoAlign(el, options)
    })

    if (resizeObserver) {
      el._autoAlignResizeObserver = resizeObserver
    }
  },

  update(el, binding) {
    const { value } = binding

    // 获取配置（可能已更新）
    const options = getAutoAlignOptions(el, value)
    el.autoAlignOptions = options

    // 重新应用样式
    setTimeout(() => {
      applyAutoAlign(el, options)
    }, 0)
  },

  unbind(el) {
    // 恢复原始样式
    restoreOriginalStyles(el)

    // 移除窗口大小变化监听
    if (el._autoAlignResizeHandler) {
      window.removeEventListener('resize', el._autoAlignResizeHandler)
      delete el._autoAlignResizeHandler
    }

    // 断开 ResizeObserver
    if (el._autoAlignResizeObserver) {
      el._autoAlignResizeObserver.disconnect()
      delete el._autoAlignResizeObserver
    }

    // 清理存储的数据
    delete el.autoAlignOptions
  }
}

export default autoAlignDirective
